import { Download, Share, SquarePlus, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { DRAWER_APPEARANCE } from '../ui/types/drawer-variants-enums';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/browser/use-is-mobile';
import { analytics } from '@/lib/analytics/analytics';
import {
  ANALYTICS_EVENT,
} from '@/lib/analytics/types/analytics-enums';
import {
  isAlreadyInstalled,
  isAndroidDismissActive,
  isIosDismissActive,
  recordIosDismiss,
} from '@/lib/pwa/install-storage';
import { useInstallPrompt } from '@/hooks/pwa/use-install-prompt';
import { useIsStandalone } from '@/hooks/pwa/use-is-standalone';
import { usePlatformInfo } from '@/hooks/pwa/use-platform';

const ENGAGEMENT_DELAY_MS = 30_000;

export function InstallBanner() {
  const isMobile = useIsMobile();
  const isStandalone = useIsStandalone();
  const platform = usePlatformInfo();
  const { canPrompt, prompt, dismissForNow } = useInstallPrompt();
  const [readyToShow, setReadyToShow] = useState<boolean>(false);
  const [showIosSheet, setShowIosSheet] = useState<boolean>(false);
  // Reactive mirror of `isIosDismissActive()`. Without this, clicking the X
  // on the iOS banner only writes to localStorage — React doesn't re-render
  // and the banner stays visible until the next page load.
  const [iosDismissed, setIosDismissed] = useState<boolean>(() => isIosDismissActive());

  useEffect(() => {
    const timer = window.setTimeout(() => setReadyToShow(true), ENGAGEMENT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const handleIosDismiss = useCallback(() => {
    recordIosDismiss();
    setIosDismissed(true);
    analytics.track(ANALYTICS_EVENT.PWA_INSTALL_PROMPT_DISMISSED, { platform: 'ios' });
  }, []);

  const handleIosShowInstructions = useCallback(() => {
    setShowIosSheet(true);
    analytics.track(ANALYTICS_EVENT.PWA_IOS_INSTRUCTIONS_OPENED, {});
  }, []);

  const handleAndroidInstall = useCallback(() => {
    analytics.track(ANALYTICS_EVENT.PWA_INSTALL_PROMPT_SHOWN, { platform: 'android' });
    void prompt();
  }, [prompt]);

  if (!isMobile || isStandalone || !readyToShow || isAlreadyInstalled()) {
    return null;
  }

  if (platform.isIosSafari && !iosDismissed) {
    return (
      <>
        <BannerCard
          actionLabel="Show me how"
          icon={<Share className="h-5 w-5" aria-hidden="true" />}
          message="Install Upstack on your iPhone for a faster, full-screen experience."
          onAction={handleIosShowInstructions}
          onDismiss={handleIosDismiss}
        />
        <IosInstallSheet onOpenChange={setShowIosSheet} open={showIosSheet} />
      </>
    );
  }

  if (platform.isAndroid && canPrompt && !isAndroidDismissActive()) {
    return (
      <BannerCard
        actionLabel="Install"
        icon={<Download className="h-5 w-5" aria-hidden="true" />}
        message="Install Upstack on your phone for a faster, full-screen experience."
        onAction={handleAndroidInstall}
        onDismiss={dismissForNow}
      />
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Banner card
// ---------------------------------------------------------------------------

interface BannerCardProps {
  message: string;
  actionLabel: string;
  icon: React.ReactNode;
  onAction: () => void;
  onDismiss: () => void;
}

function BannerCard({ message, actionLabel, icon, onAction, onDismiss }: BannerCardProps) {
  return (
    <div
      className="fixed inset-x-3 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-30 md:hidden"
      role="region"
      aria-label="Install Upstack as an app"
    >
      <div className="flex items-center gap-3 rounded-xl border border-border bg-background p-3 shadow-card-elevated">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </span>
        <p className="flex-1 text-sm leading-snug text-foreground">{message}</p>
        <Button onClick={onAction} size="sm">
          {actionLabel}
        </Button>
        <Button
          aria-label="Dismiss install prompt"
          className="h-8 w-8 shrink-0 text-foreground/60 hover:text-foreground"
          onClick={onDismiss}
          size="icon"
          variant="ghost"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// iOS instructional sheet
// ---------------------------------------------------------------------------

interface IosInstallSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function IosInstallSheet({ open, onOpenChange }: IosInstallSheetProps) {
  return (
    <Drawer onOpenChange={onOpenChange} open={open} shouldScaleBackground>
      <DrawerContent appearance={DRAWER_APPEARANCE.FOUNDATION} className="max-h-[85vh]">
        <DrawerHeader>
          <DrawerTitle>Add Upstack to your Home Screen</DrawerTitle>
        </DrawerHeader>
        <div className="flex flex-col gap-5 px-6 pb-2">
          <p className="text-sm leading-relaxed text-content-secondary">
            Installing Upstack gives you a full-screen, app-like experience —
            faster launches, smoother navigation, and a home-screen icon.
          </p>
          <ol className="flex flex-col gap-4">
            <InstructionStep
              icon={<Share className="h-5 w-5" aria-hidden="true" />}
              step={1}
              title="Tap the Share icon"
              description="It's the box-with-an-arrow icon in Safari's bottom toolbar."
            />
            <InstructionStep
              icon={<SquarePlus className="h-5 w-5" aria-hidden="true" />}
              step={2}
              title="Choose “Add to Home Screen”"
              description="Scroll down in the Share sheet if you don't see it."
            />
            <InstructionStep
              step={3}
              title="Tap “Add”"
              description="The Upstack icon will appear on your Home Screen, ready to launch."
            />
          </ol>
        </div>
        <DrawerFooter className="pt-4">
          <DrawerClose asChild>
            <Button className="w-full" variant="outline">
              Got it
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

interface InstructionStepProps {
  step: number;
  title: string;
  description: string;
  icon?: React.ReactNode;
}

function InstructionStep({ step, title, description, icon }: InstructionStepProps) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {step}
      </span>
      <div className="flex-1">
        <p className="flex items-center gap-2 text-sm font-semibold text-content-primary">
          {title}
          {icon ? (
            // Intentional brand icon well — Safari chrome callout, not routine surface wash.
            <span
              aria-hidden="true"
              className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary"
            >
              {icon}
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-sm leading-snug text-content-secondary">{description}</p>
      </div>
    </li>
  );
}
